// Chakra imports
import { toastError } from "../../../../utils/showApiErrorToast";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  useColorModeValue,
  useBreakpointValue,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Box,
  Text,
  VStack,
} from "@chakra-ui/react";
import React, { useEffect } from "react";
import { toast } from "sonner";
// Custom components
import TradeForm from "./TradeForm";
// Types
import { TradeFormData } from "../../../../types/trade";

interface LogTradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Local `pending-*` id for new trades, or the trade UUID when editing (attachment localStorage key). */
  attachmentStorageId: string;
  initialData?: Partial<TradeFormData>;
  onSave?: (data: TradeFormData) => void;
  isEditMode?: boolean;
}

const LogTradeModal: React.FC<LogTradeModalProps> = ({
  isOpen,
  onClose,
  attachmentStorageId,
  initialData,
  onSave,
  isEditMode = false,
}) => {
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const formRef = React.useRef<HTMLFormElement>(null);
  const secondaryTextColor = useColorModeValue("gray.400", "white");
  const modalBg = useColorModeValue("white", "#151517");
  const modalSize = useBreakpointValue({ base: "full", md: "xl", lg: "4xl", xl: "5xl" }) ?? "4xl";

  // Match TradeDetailsModal transition styles (separate style id so it does not remove details styles)
  useEffect(() => {
    const styleId = "log-trade-modal-animation";
    const existingStyle = document.getElementById(styleId);
    if (existingStyle) {
      existingStyle.remove();
    }

    const style = document.createElement("style");
    style.id = styleId;
    style.textContent = `
      [data-chakra-modal-content] {
        transition: opacity 0.25s cubic-bezier(0.4, 0, 0.2, 1), 
                    transform 0.25s cubic-bezier(0.4, 0, 0.2, 1) !important;
      }
      [data-chakra-modal-overlay] {
        transition: opacity 0.2s cubic-bezier(0.4, 0, 0.2, 1), 
                    backdrop-filter 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important;
      }
      [data-chakra-modal-content][data-exit],
      [data-chakra-modal-content].chakra-modal__content[data-exit] {
        opacity: 0 !important;
        transform: scale(0.95) translateY(10px) !important;
        transition: opacity 0.2s cubic-bezier(0.4, 0, 0.2, 1), 
                    transform 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important;
      }
      [data-chakra-modal-overlay][data-exit],
      [data-chakra-modal-overlay].chakra-modal__overlay[data-exit] {
        opacity: 0 !important;
        transition: opacity 0.15s cubic-bezier(0.4, 0, 0.2, 1), 
                    backdrop-filter 0.15s cubic-bezier(0.4, 0, 0.2, 1) !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      const styleToRemove = document.getElementById(styleId);
      if (styleToRemove) {
        styleToRemove.remove();
      }
    };
  }, []);

  const handleSubmit = async (data: TradeFormData) => {
    setIsSubmitting(true);

    try {
      await new Promise((resolve) => setTimeout(resolve, 500));

      toast.success(isEditMode ? "Trade updated successfully" : "Trade logged successfully", {
        description: `${data.symbol} trade has been ${isEditMode ? "updated" : "logged"}.`,
      });

      if (onSave) {
        onSave(data);
      }

      onClose();
    } catch (error) {
      toastError("Error logging trade", {
        description: "An error occurred while logging the trade. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    onClose();
  };

  const handleFormSubmit = () => {
    if (formRef.current) {
      formRef.current.requestSubmit();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={modalSize}
      isCentered
      scrollBehavior="inside"
      motionPreset="scale"
    >
      <ModalOverlay backdropFilter="blur(10px)" bg="rgba(0, 0, 0, 0.1)" />
      <ModalContent
        maxH="90vh"
        bg={modalBg}
        // @ts-ignore - motionProps supported by Chakra ModalContent
        motionProps={{
          initial: { opacity: 0, scale: 0.95, y: 20 },
          animate: { opacity: 1, scale: 1, y: 0 },
          exit: { opacity: 0, scale: 0.95, y: 10 },
          transition: {
            duration: 0.25,
            ease: [0.4, 0, 0.2, 1],
          },
        }}
      >
        <ModalHeader px={6} pt={6} pb={4}>
          <VStack align="stretch" spacing={3}>
            <Text
              color="#38BBD1"
              fontFamily="'Urbanist', sans-serif"
              fontWeight="normal"
              fontSize="xl"
            >
              {isEditMode ? "Edit Trade" : "Log New Trade"}
            </Text>
          </VStack>
        </ModalHeader>
        <ModalCloseButton top={2} right={2} />
        <ModalBody overflowY="auto" maxH="calc(90vh - 140px)" px={6} pb={6} pt={0}>
          {initialData?.tags?.includes("tradar") && (
            <Alert
              status="info"
              borderRadius="md"
              mb={4}
              bg="rgba(56, 187, 209, 0.1)"
              border="1px solid"
              borderColor="rgba(56, 187, 209, 0.3)"
            >
              <AlertIcon color="#38BBD1" />
              <Box flex="1">
                <AlertTitle fontSize="sm" color="#38BBD1" fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium">
                  Suggested Values from Signal
                </AlertTitle>
                <AlertDescription fontSize="xs" color="gray.600" fontFamily="'IBM Plex Sans', sans-serif" mt={1}>
                  These values are suggested from the Tradar signal. Please verify and update with your actual executed trade values before saving.
                </AlertDescription>
              </Box>
            </Alert>
          )}
          <TradeForm
            ref={formRef}
            attachmentStorageId={attachmentStorageId}
            initialData={initialData}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isLoading={isSubmitting}
          />
        </ModalBody>
        <ModalFooter px={6} pb={6} pt={0}>
          <Button
            variant="ghost"
            mr={3}
            onClick={handleCancel}
            isDisabled={isSubmitting}
            fontFamily="'IBM Plex Sans', sans-serif"
            fontSize="xs"
            color={secondaryTextColor}
            fontWeight="medium"
            textTransform="uppercase"
          >
            Cancel
          </Button>
          <Button
            bgGradient="linear-gradient(135deg, #38BBD1, #3EEAEC)"
            color="white"
            _hover={{
              bgGradient: "linear-gradient(135deg, #38BBD1, #3EEAEC)",
              opacity: 0.9,
            }}
            onClick={handleFormSubmit}
            isLoading={isSubmitting}
            loadingText="Saving..."
            fontFamily="'IBM Plex Sans', sans-serif"
            fontSize="xs"
            fontWeight="medium"
            textTransform="uppercase"
          >
            {isEditMode ? "Update Trade" : "Save Trade"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default LogTradeModal;
